import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

type Row = { id: string; parentName: string; studentName?: string; hasUnread?: boolean; lastMessageText: string; lastMessageAt: number; studentKeys?: string[] };
type Message = { id: string; text: string; senderDisplayName?: string; senderId: string; createdAt: number; officeAuthor?: string; attachments?: { name: string; downloadUrl?: string }[] };
const stamp = (n: number) => n ? new Date(n).toLocaleString() : "";
const call = async (data: object): Promise<any> => (await httpsCallable(functions, "officeInbox")(data)).data;

export function OfficeInboxPage() {
  const [params, setParams] = useSearchParams();
  const selected = params.get("threadId") || "";
  const [threads, setThreads] = useState<Row[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [copied, setCopied] = useState(false);
  const [copyPrincipal, setCopyPrincipal] = useState(false);
  const [nextThreads, setNextThreads] = useState<string | null>(null);
  const [nextMessages, setNextMessages] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const retry = useRef<{ threadId: string; text: string; requestId: string } | null>(null);
  const selection = useRef(selected);
  const olderLoaded = useRef(false);
  const moreThreadsLoaded = useRef(false);
  selection.current = selected;
  useEffect(() => {
    let live = true;
    const refresh = async () => {
      try {
        const result = await call({});
        if (!live) return;
        setThreads(old => [...result.threads, ...old.filter(t => !result.threads.some((r: Row) => r.id === t.id))]);
        if (!moreThreadsLoaded.current) setNextThreads(result.next);
        setCopied(result.copied); setCopyPrincipal(result.copyPrincipal);
      } catch (e: any) { if (live) { setError(e.message); setThreads([]); setMessages([]); } }
      finally { if (live) setLoading(false); }
    };
    void refresh(); const timer = window.setInterval(refresh, 15000);
    return () => { live = false; window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    setMessages([]); setText(""); setNextMessages(null);
    olderLoaded.current = false;
    if (!selected) return;
    let live = true;
    const refresh = async () => {
      try {
        const result = await call({ threadId: selected });
        if (!live) return;
        setMessages(old => [...old.filter(m => !result.messages.some((r: Message) => r.id === m.id)), ...result.messages].sort((a, b) => a.createdAt - b.createdAt));
        if (!olderLoaded.current) setNextMessages(result.next);
        setCopied(result.copied); setCopyPrincipal(result.copyPrincipal);
      } catch (e: any) { if (live) { setError(e.message); setMessages([]); } }
    };
    void refresh(); const timer = window.setInterval(refresh, 10000);
    return () => { live = false; window.clearInterval(timer); };
  }, [selected]);
  async function send() {
    if (!text.trim() || sending) return;
    setSending(true); setError("");
    const payload = retry.current?.text === text.trim() && retry.current.threadId === selected
      ? retry.current : { threadId: selected, text: text.trim(), requestId: crypto.randomUUID() };
    retry.current = payload;
    try {
      await httpsCallable(functions, "replyToOfficeMessage")(payload);
      retry.current = null;
      if (selection.current !== payload.threadId) return;
      setText(""); const result = await call({ threadId: payload.threadId });
      if (selection.current === payload.threadId) setMessages(old => [...old.filter(m => !result.messages.some((r: Message) => r.id === m.id)), ...result.messages].sort((a, b) => a.createdAt - b.createdAt));
    } catch (e: any) { setError(e.message || "Unable to send. You can retry safely."); }
    finally { setSending(false); }
  }
  return <div className="page"><h1>School Office</h1>
    <p>{copied ? "Principal copied — these messages were sent to School Office. You may reply on behalf of the office." : "Shared inbox for the Office role."} {copyPrincipal && !copied ? "The principal is copied on these conversations." : ""}</p>
    <p className="muted">Every reply is shown to parents as School Office. The staff author is recorded internally. Inbox refreshes automatically.</p>
    {error && <div role="alert" className="card">{error}</div>}
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 20 }}>
      <div className="card" style={{ flex: "1 1 280px", padding: 16 }}>
        <h2>Conversations</h2>{loading ? <p>Loading…</p> : !threads.length && <p>No office conversations yet.</p>}
        {threads.map(t => <button key={t.id} disabled={sending} onClick={() => setParams({ threadId: t.id })} style={{ display: "block", width: "100%", textAlign: "left", padding: 14, marginBottom: 8, border: selected === t.id ? "2px solid var(--primary)" : "1px solid var(--border)", borderRadius: 8, color: "var(--text)", background: selected === t.id ? "var(--primary-soft)" : "var(--card)", cursor: "pointer" }}>
          <strong>{t.hasUnread ? "● " : ""}{t.parentName}</strong>{t.studentName && <div>About {t.studentName}</div>}<div>{t.lastMessageText || "New conversation"}</div><small>{stamp(t.lastMessageAt)}</small>
        </button>)}
        {nextThreads && <button className="btn" onClick={async () => { try {
          const r = await call({ before: nextThreads }); moreThreadsLoaded.current = true; setThreads(old => [...old, ...r.threads.filter((t: Row) => !old.some(o => o.id === t.id))]); setNextThreads(r.next);
        } catch (e: any) { setError(e.message); } }}>Load more conversations</button>}
      </div>
      <div className="card" style={{ flex: "2 1 400px", padding: 20 }}>
        {!selected ? <p>Select a conversation to read and reply.</p> : <>
          <h2>{threads.find(t => t.id === selected)?.parentName || "Office conversation"}</h2>
          {nextMessages && <button className="btn" onClick={async () => { const id = selected; try {
            const r = await call({ threadId: id, before: nextMessages }); if (selection.current !== id) return;
            olderLoaded.current = true; setMessages(old => [...r.messages.filter((m: Message) => !old.some(o => o.id === m.id)), ...old]); setNextMessages(r.next);
          } catch (e: any) { setError(e.message); } }}>Load earlier messages</button>}
          <div aria-live="polite" style={{ maxHeight: "55vh", overflowY: "auto", margin: "16px 0" }}>
            {messages.map(m => <article key={m.id} style={{ padding: 14, marginBottom: 12, borderRadius: 10, color: "var(--text)", background: m.senderId === "__school_office__" ? "var(--primary-soft)" : "var(--surface)" }}>
              <strong>{m.senderId === "__school_office__" ? "School Office" : m.senderDisplayName || "Parent"}</strong>
              {m.officeAuthor && <span className="muted"> · Sent by {m.officeAuthor} (staff only)</span>}
              <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{m.text}</div>
              {m.attachments?.map((a, i) => a.downloadUrl?.startsWith("https://") ? <div key={i}><a href={a.downloadUrl} target="_blank" rel="noreferrer">{a.name || "Attachment"}</a></div> : <div key={i}>{a.name || "Attachment"}</div>)}
              <small className="muted">{stamp(m.createdAt)}</small>
            </article>)}
          </div>
          <label htmlFor="office-reply">Reply as School Office</label>
          <textarea className="input" id="office-reply" value={text} maxLength={10000} disabled={sending} onChange={e => setText(e.target.value)} rows={4} style={{ display: "block", width: "100%", margin: "8px 0" }} />
          <button className="btn" disabled={!text.trim() || sending} onClick={send}>{sending ? "Sending…" : "Send as School Office"}</button>
        </>}
      </div>
    </div>
  </div>;
}
